import { useWorkspaceDataflowId } from "@/features/workspace/context/WorkspaceContext";
import { actionMapping, createActionRegistry } from "@/stores/actions";
import { useDataflowStore } from "@/stores/dataflow";
import { useUIStore } from "@/stores/ui";
import { useCallback, useMemo } from "react";

const TRANSFORM_DELAY_MS = 300;

function updateDataflowsFromResult(result: any) {
  if (result && "dataflows" in result && result.dataflows) {
    Object.entries(result.dataflows).forEach(([id, data]) => {
      useDataflowStore.getState().updateDataflow(id, data as any);
    });
  }
}

function handleActionResult(result: any, dataflowId: string | undefined) {
  if (!result || Object.keys(result).length === 0) return;

  const uiStore = useUIStore.getState();

  if (
    "sidebar" in result &&
    result.sidebar &&
    typeof result.sidebar === "object"
  ) {
    const sidebar = result.sidebar as { isOpen?: boolean; mode?: string };
    if (sidebar.isOpen && sidebar.mode) {
      uiStore.openSidebar(dataflowId, sidebar.mode as any);
    } else if (sidebar.isOpen === false) {
      uiStore.closeSidebar(dataflowId);
    }
  }

  if ("modal" in result && result.modal && typeof result.modal === "object") {
    const modal = result.modal as {
      isOpen?: boolean;
      mode?: string;
      title?: string;
      data?: unknown;
    };
    if (modal.isOpen && modal.mode) {
      uiStore.openModal(dataflowId, modal.mode as any, modal.title, modal.data);
    } else if (modal.isOpen === false) {
      uiStore.closeModal(dataflowId);
    }
  }

  if (
    "view" in result &&
    result.view !== undefined &&
    typeof result.view === "string"
  ) {
    uiStore.setView(dataflowId, result.view as any);
  }

  updateDataflowsFromResult(result);
}

function handleTransformAction(
  actionId: string,
  dataflowId: string | undefined,
  actionRegistry: Map<string, any>
) {
  const uiStore = useUIStore.getState();
  const workspaceUI = uiStore.getWorkspaceUI(dataflowId);
  const wasSidebarOpen =
    workspaceUI.sidebar.isOpen && workspaceUI.sidebar.mode === "flow";

  uiStore.openSidebar(dataflowId, "flow");

  const transformAction = actionMapping[actionId];
  if (!transformAction) return;

  const handler = actionRegistry.get(transformAction);
  if (!handler) return;

  const executeHandler = () => {
    const result = handler(
      () => useDataflowStore.getState(),
      () => useUIStore.getState(),
      {}
    );

    if (result instanceof Promise) {
      result.then(updateDataflowsFromResult);
    } else {
      updateDataflowsFromResult(result);
    }
  };

  if (wasSidebarOpen) {
    executeHandler();
  } else {
    setTimeout(executeHandler, TRANSFORM_DELAY_MS);
  }
}

/**
 * Hook to handle workspace actions
 * @returns The action handler function
 */
export function useActionHandler() {
  const actionRegistry = useMemo(() => createActionRegistry(), []);
  const dataflowId = useWorkspaceDataflowId();

  const actionHandler = useCallback(
    async (actionId: string, parentId?: string) => {
      if (parentId) {
        switch (parentId) {
          case "view":
            useUIStore.getState().setView(dataflowId, actionId as any);
            return;
          case "transform":
            handleTransformAction(actionId, dataflowId, actionRegistry);
            return;
        }
        const mappedAction = actionMapping[actionId];
        if (mappedAction) {
          const handler = actionRegistry.get(mappedAction);
          if (handler) {
            const result = await handler(
              () => useDataflowStore.getState(),
              () => useUIStore.getState(),
              { actionId }
            );
            handleActionResult(result, dataflowId);
          }
        }
        return;
      }

      const mappedAction = actionMapping[actionId];
      if (!mappedAction) return;

      const handler = actionRegistry.get(mappedAction);
      if (!handler) return;

      const result = await handler(
        () => useDataflowStore.getState(),
        () => useUIStore.getState(),
        { actionId }
      );

      handleActionResult(result, dataflowId);
    },
    [actionRegistry, dataflowId]
  );

  return actionHandler;
}
